import { is, List } from "immutable";
import * as React from "react";
import * as BS from "react-bootstrap";
import * as BT from "react-bootstrap-table";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import * as M from "../../../models";
import { AppState } from "../../../store";
import { ReactHelper, Utility } from "../../../utils";

type DataRow = M.Abstract.GradingGroup;

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultListViewStatePropsMap(state, ownProps),
    groups: state.adminAbstracts.groups,
    loadingGroups: state.adminAbstracts.loadingGroups,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadGroups: (eventId: number) => dispatch(Actions.AdminAbstract.loadGroupsForEvent(eventId)),
    toggleGroupsLoading: (eventId: number, loading: boolean) => dispatch(Actions.AdminAbstract.toggleLoadingGroups(eventId, loading)),
});

type OwnProps = RouteComponentProps<{}> & {
    eventId: number;
};

type ConnectedState = Comp.Base.DefaultListViewConnectedState & {
    groups: List<M.Abstract.GradingGroup>;
    loadingGroups: M.Generic.ItemLoadingState;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadGroups: (eventId: number) => void;
    toggleGroupsLoading: (eventId: number, loading: boolean) => void;
};

interface OwnState {
    groups: List<DataRow>;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class ListGroupsComponent extends Comp.Base.BaseListViewComponent<CombinedProps, OwnState> {

    public constructor(props: any) {
        super(props);
        this.state = {
            ...this.getInitialListViewState(props),
            groups: List(),
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
        await this.initializeComponent();
    }

    public componentDidUpdate(prevProps: Readonly<CombinedProps>) {
        if (!is(prevProps.groups, this.props.groups))
            this.loadGroupsFromState();

        // just in case a different guid in URL has been detected
        if (prevProps.eventId !== this.props.eventId) {
            this.clearState(() => {
                this.initializeComponent();
            });
        }
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        const eventId = this.props.eventId;
        const loading = this.state.loading || !!this.props.loadingGroups[eventId];

        const actionsPrimary = <BS.ButtonToolbar>
            <BS.ButtonGroup>
                <Link className="btn btn-default" to={Paths.AdminAbstractListGroupEdit(this.props.eventId, 0)}>
                    <Comp.Generic.Icon icon="plus" /> Add group
                </Link>
            </BS.ButtonGroup>
        </BS.ButtonToolbar>;

        const tableActions = this.getTableActions({
            changeLength: false,
            changePage: false,
            dataLength: this.state.groups.size,
            filteredLength: this.state.groups.size,
            initialPageSize: this.state.groups.size,
            pageSize: this.state.groups.size,
            searchPlaceholder: "Search groups...",
        });

        const abstractListLink = this.lastPathIsFromListView()
            ? <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); window.history.back(); }}>Abstracts</a>
            : <Link to={Paths.AdminAbstractList(eventId)}>Abstracts</Link>;

        return <Comp.Page.AdminPage actionsPrimary={actionsPrimary} actionsSecondary={tableActions} title={"Abstracts"}>

            {this.getErrorAlert()}

            <Comp.Generic.LoadingSpinner show={loading} />

            <h1>
                {abstractListLink} &raquo; Grading groups
            </h1>

            <BT.BootstrapTable
                bordered={false}
                data={this.state.groups.toArray()}
                pagination={this.state.tablePageSize > -1}
                options={{
                    afterSearch: (searchText, results) => this.onTableSearch(searchText, results),
                    page: this.state.tablePage,
                    // pagination: false,
                    paginationPanel: () => <div />,
                    sizePerPage: this.state.groups.size, // this.state.tablePageSize,
                    onSortChange: (sortName: any, sortOrder: any) => this.handleTableSort(sortOrder, sortName),
                    defaultSortName: "name",
                    defaultSortOrder: "asc",
                }}
                ref={(ref: any) => this.table = ref}
            >
                <BT.TableHeaderColumn dataField="id" isKey hidden></BT.TableHeaderColumn>

                <BT.TableHeaderColumn
                    dataField="name"
                    dataFormat={(cell: any, row: DataRow) => (<Link to={Paths.AdminAbstractListGroupEdit(row.eventId, row.id)}>{row.name}</Link>)}
                    dataSort
                    width="25%"
                >Name</BT.TableHeaderColumn>

                <BT.TableHeaderColumn dataField="parent" dataSort width="25%">Parent</BT.TableHeaderColumn>

                <BT.TableHeaderColumn
                    dataField="reviewers"
                    dataFormat={(cell: any, row: DataRow) => row.reviewers.length.toString()}
                    dataSort
                >Reviewers</BT.TableHeaderColumn>

                <BT.TableHeaderColumn
                    dataField="opensOn"
                    dataFormat={(cell: any, row: DataRow) => Utility.fromApiDateAsMoment(row.opensOnServer).format("MM/DD/YYYY")}
                    dataSort
                >Opens on</BT.TableHeaderColumn>

                <BT.TableHeaderColumn
                    dataField="closesOn"
                    dataFormat={(cell: any, row: DataRow) => Utility.fromApiDateAsMoment(row.closesOnServer).format("MM/DD/YYYY")}
                    dataSort
                >Closes on</BT.TableHeaderColumn>
            </BT.BootstrapTable>

        </Comp.Page.AdminPage>;
    }

    private clearState(callback?: () => void) {
        this.setState({
            groups: List(),
        }, callback);
    }

    private async initializeComponent() {
        const eventId = this.props.eventId;

        await this.loadGroupsFromState(true);
        this.props.loadGroups(eventId);
    }

    private lastPathIsFromListView() {
        return this.props.lastPath === `${Paths.AdminAbstracts.BasePath}/${this.props.eventId}`
            || (this.props.lastPath || "").indexOf(`${Paths.AdminAbstracts.BasePath}/${this.props.eventId}?`) === 0;
    }

    private async loadGroupsFromState(toggleLoading: boolean = false) {
        const eventId = this.props.eventId;
        const groups = this.props.groups.filter((x: M.Abstract.GradingGroup) => x.eventId === eventId).toList();

        await this.setStateAsync({ ...this.state, groups });

        if (!groups.size && toggleLoading)
            this.props.toggleGroupsLoading(eventId, true);

        // update table state from location state if available
        this.updateTableState(groups);
    }
}

export const ListGroups: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(ListGroupsComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/adminAbstracts/listGroups/ListGroups.tsx